// Saving the week's sessions before the athlete walks into a gym with no signal.
//
// # THE DEFECT
//
// A session could be opened offline only if the athlete had already opened it
// online (sessionSnapshot). Today's session, on a phone that last saw a network
// in the car park, opened to "Could not load this session." The service worker
// cannot help: it never caches /api, because every response is resolved against
// ONE reader's maxes (swRoutes.js). So the copy has to be made here, per user,
// under the same keys the logger already reads and logout already clears.
//
// # THE BUDGET
//
// The home screen is the busiest screen in the app and this runs behind it, on
// whatever network the athlete has. So: today and the next six days only, each
// session once however many dates it falls on, never more than MAX_FETCHES, no
// more than CONCURRENCY at a time, and nothing whose copy is still fresh. Plus
// one phase list per programme in the window (usually one or two), so the
// logger's week selector works offline as well.
//
// # WHOSE COPY
//
// A fetch started for one athlete must never be saved after another has signed
// in on the same phone, or after logout cleared the first one's copies. The
// caller aborts on unmount and on any change of user; `isCurrent` is checked
// again just before every save, because a response can land between logout
// clearing storage and React running the effect cleanup.

import * as snapshot from './sessionSnapshot';
import { todayISO } from '../schedule';

export const WINDOW_DAYS = 7;
export const MAX_FETCHES = 7;
export const CONCURRENCY = 2;
// Six hours: a morning visit covers an evening session. Older than that, a
// coach's edit or a retest is plausible enough to be worth one request.
export const FRESH_MS = 6 * 60 * 60 * 1000;

/** The UTC calendar date of a timestamp: models.CalendarDate's day. */
export const utcISO = (ms) => new Date(ms).toISOString().slice(0, 10);

/** YYYY-MM-DD plus n calendar days, in the reader's own calendar. */
export function addDaysISO(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return todayISO(new Date(y, m - 1, d + n));
}

/**
 * Whether a saved copy is recent enough not to fetch again.
 *
 * Also stale when made on another calendar day: the /train payload carries the
 * week the athlete is in (currentWeek), which a copy from yesterday can have
 * wrong. A UTC day, because that is the day the server computes it for. And
 * stale when it claims to come from the future — a phone clock set back would
 * otherwise keep a copy "fresh" indefinitely.
 */
export function isFresh(saved, now = Date.now()) {
  if (!saved || typeof saved.savedAt !== 'number') return false;
  const age = now - saved.savedAt;
  if (age < 0 || age >= FRESH_MS) return false;
  return utcISO(saved.savedAt) === utcISO(now);
}

/**
 * Which sessions to fetch: [{ programId, sessionId }], soonest first.
 *
 * Entries outside today..today+6 are ignored, a session on several dates (or on
 * two assignments of the same programme) is fetched once, a fresh copy is not
 * fetched at all, and the list never exceeds `max`.
 */
export function planPrefetch(entries, {
  user,
  today = todayISO(),
  now = Date.now(),
  load = snapshot.load,
  max = MAX_FETCHES,
} = {}) {
  if (!user || !Array.isArray(entries)) return [];
  const last = addDaysISO(today, WINDOW_DAYS - 1);
  const inWindow = entries
    .filter((e) => e && e.programId && e.sessionId && e.date >= today && e.date <= last)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const seen = new Set();
  const plan = [];
  for (const e of inWindow) {
    if (plan.length >= max) break;
    const key = `${e.programId}|${e.sessionId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (isFresh(load(user, e.programId, e.sessionId), now)) continue;
    plan.push({ programId: e.programId, sessionId: e.sessionId });
  }
  return plan;
}

/**
 * Which programmes' phase lists to fetch: the distinct programmes in the
 * window whose saved list is not fresh. The logger's week selector needs the
 * block length, and a prefetched session opened offline without it could not
 * be moved off a wrong week.
 */
export function planPhases(entries, {
  user,
  today = todayISO(),
  now = Date.now(),
  load = snapshot.loadPhases,
  max = MAX_FETCHES,
} = {}) {
  if (!user || !Array.isArray(entries)) return [];
  const last = addDaysISO(today, WINDOW_DAYS - 1);
  const ids = [];
  for (const e of entries) {
    if (!e || !e.programId || !e.phaseId || e.date < today || e.date > last) continue;
    if (ids.includes(e.programId) || ids.length >= max) continue;
    if (isFresh(load(user, e.programId), now)) continue;
    ids.push(e.programId);
  }
  return ids;
}

const DAY_MS = 86_400_000;
// Days since 1970-01-01 for a YYYY-MM-DD, and the Sunday that starts its week:
// models.WeekStart, on day numbers. 1970-01-01 was a Thursday (4).
const dayNumber = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
};
const weekStart = (n) => n - ((((n + 4) % 7) + 7) % 7);

/**
 * The week a saved session should open on, today, and whether that is known.
 * Returns { week, certain }.
 *
 * The /train payload's currentWeek is the server's answer
 * (models.TrainingWeekFor) for the UTC day it was FETCHED. A copy prefetched on
 * Friday and opened offline on Sunday sits across a week boundary, and opening
 * it on last week's number would show last week's prescriptions and file the
 * sets under it.
 *
 * The payload also carries weekBasis: the start date and block length of the
 * assignment the server counted from — the CURRENT version (the newest active
 * assignment of the programme, or a replaced version until its successor
 * starts, given as `until`). Counting from anything else is a guess: calendar
 * entries do not say which assignment or version they belong to, and the
 * newest one may start outside the calendar's window. So:
 *
 *  - once a replaced version's `until` has come, 0, whatever the day of the
 *    week (a successor can start midweek);
 *  - otherwise, within the save's own Sunday-to-Saturday week (UTC) the copy's
 *    week is exact, since nothing the server counts from can have moved;
 *  - across a boundary, the week is counted from weekBasis exactly as the
 *    server counts it: Sunday-anchored from the start, 1 before it, the last
 *    week after the block;
 *  - with no basis (a copy saved before the server sent one) the copy's own
 *    week, marked uncertain, so the logger asks the athlete to check it.
 *
 * What cannot be known offline is a change made after the save (a new version
 * published since); that is the price of a saved copy, and why it is labelled.
 * 0 — no schedule at all — stays 0, which the logger reads as unknown.
 */
export function offlineWeek(saved, { today = utcISO(Date.now()) } = {}) {
  const own = saved?.data?.currentWeek ?? 0;
  if (!saved || !(own > 0)) return { week: own, certain: true };
  const basis = saved.data.weekBasis;
  // First, before the same-week shortcut: a replaced version stops on its
  // successor's start, which can be any weekday, so a copy saved on Monday is
  // not valid all week.
  if (basis?.until && today >= basis.until) return { week: 0, certain: true };

  const now = dayNumber(today);
  const weeks = basis?.blockLengthWeeks;
  if (!basis || !basis.startDate || !(weeks > 0)) {
    // No basis: the copy's own week is exact only inside the week it was saved
    // in. (savedAt is when the response ARRIVED, so a request straddling a
    // UTC Sunday can be a second out; with a basis the count below is used
    // instead, and does not depend on savedAt at all.)
    const savedOn = dayNumber(utcISO(saved.savedAt));
    return { week: own, certain: weekStart(savedOn) === weekStart(now) };
  }

  const start = dayNumber(basis.startDate);
  if (now < start) return { week: 1, certain: true };
  const week = Math.floor((now - weekStart(start)) / 7) + 1;
  return { week: Math.min(week, weeks), certain: true };
}

/**
 * Fetch and save the planned sessions, CONCURRENCY at a time.
 *
 * `fetchTrain(programId, sessionId, signal)` resolves to the /train payload.
 * `fetchPhases(programId, signal)`, when given, resolves to a phase list; one
 * per programme in the window, after the sessions, in the same pool.
 * A failed fetch is skipped, not retried: this is a convenience, and the
 * logger still opens online. Resolves to a summary; never rejects.
 */
export async function runPrefetch({
  user,
  entries,
  fetchTrain,
  fetchPhases = null,
  signal,
  isCurrent = () => true,
  today = todayISO(),
  now = Date.now,
  load = snapshot.load,
  save = snapshot.save,
  loadPhases = snapshot.loadPhases,
  savePhases = snapshot.savePhases,
  concurrency = CONCURRENCY,
}) {
  const summary = { planned: 0, saved: 0, failed: 0, aborted: false };
  const stopped = () => Boolean(signal?.aborted) || !isCurrent();
  if (!user || !fetchTrain || stopped()) {
    summary.aborted = stopped();
    return summary;
  }

  const plan = planPrefetch(entries, { user, today, now: now(), load }).map((item) => ({
    ...item,
    fetch: () => fetchTrain(item.programId, item.sessionId, signal),
    store: (data) => save(user, item.programId, item.sessionId, data, { now }),
  }));
  if (fetchPhases) {
    planPhases(entries, { user, today, now: now(), load: loadPhases }).forEach((programId) => plan.push({
      programId,
      fetch: () => fetchPhases(programId, signal),
      store: (data) => savePhases(user, programId, data, { now }),
    }));
  }
  summary.planned = plan.length;

  let next = 0;
  const worker = async () => {
    while (next < plan.length) {
      if (stopped()) return;
      const task = plan[next];
      next += 1;
      let data;
      try {
        data = await task.fetch();
      } catch {
        if (!stopped()) summary.failed += 1;
        continue;
      }
      // The check that matters: saving after a user switch would file one
      // athlete's loads under the next athlete's session, or resurrect a copy
      // logout has just cleared.
      if (stopped()) return;
      if (data) {
        task.store(data);
        summary.saved += 1;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
  summary.aborted = stopped();
  return summary;
}

/**
 * No response at all (offline, DNS, a dropped connection) or the server
 * failing rather than answering. A 4xx is an answer and is never papered over
 * with a saved copy. The same rule as the logger's.
 */
export function isUnreachable(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}
